import {DropdownMenu} from '@wandb/components';
import type {Selection} from 'react-aria-components';
import {useEffect, useMemo, useState} from 'react';

import type {ShellViewer} from '../account/accountModel';
import {
  FORCE_TRACE_STORAGE_KEY,
  disableForceTrace,
  enableForceTrace,
  getForceTraceExpiresAt,
  isForceTraceActive,
  setViewerUsingAdminPrivileges,
  viewerUsingAdminPrivileges,
} from '../admin/adminState';
import type {AdminTools} from '../admin/useAdminTools';
import {
  activeRouteClaim,
  shellPathForRemotePath,
  type RouteClaimTable,
} from '../routeClaims';
import {adminEnvironments} from '../shellConfig';
import {HeaderLink} from './HeaderLink';
import {useShellHeaderActions} from './ShellHeaderContext';

const forceTraceLabel = (
  expiresAt: number | null,
  now: number
): string | null => {
  if (expiresAt == null) {
    return null;
  }
  const remaining = expiresAt - now;
  if (remaining <= 0) {
    return null;
  }
  const minutes = Math.floor(remaining / 60000);
  const seconds = Math.floor((remaining % 60000) / 1000);
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const peerUrl = (origin: string): string =>
  `${origin}${location.pathname}${location.search}${location.hash}`;

export function AdminToolsSection({
  viewer,
  adminTools,
  routeClaims,
  pathname,
  onClose,
}: {
  viewer: ShellViewer;
  adminTools: AdminTools;
  routeClaims: RouteClaimTable;
  pathname: string;
  onClose: () => void;
}) {
  const actions = useShellHeaderActions();
  const [forceTraceExpiresAt, setForceTraceExpiresAt] = useState(
    getForceTraceExpiresAt
  );
  const [now, setNow] = useState(() => Date.now());
  const [forceTraceActive, setForceTraceActive] = useState(() =>
    isForceTraceActive(viewer.email)
  );
  const [pendingAdminPowers, setPendingAdminPowers] = useState<boolean | null>(
    null
  );
  const adminPowersChecked = pendingAdminPowers ?? adminTools.isAdminModeActive;

  useEffect(() => {
    if (!forceTraceActive || forceTraceExpiresAt == null) {
      return;
    }
    const interval = window.setInterval(() => {
      const expiresAt = getForceTraceExpiresAt();
      setNow(Date.now());
      setForceTraceExpiresAt(expiresAt);
      setForceTraceActive(isForceTraceActive(viewer.email));
    }, 1000);
    return () => window.clearInterval(interval);
  }, [forceTraceActive, forceTraceExpiresAt, viewer.email]);

  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== FORCE_TRACE_STORAGE_KEY && event.key !== null) {
        return;
      }
      setForceTraceExpiresAt(getForceTraceExpiresAt());
      setNow(Date.now());
      setForceTraceActive(isForceTraceActive(viewer.email));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [viewer.email]);

  const selectedKeys = useMemo(() => {
    const keys = new Set<string>();
    if (adminPowersChecked) {
      keys.add('admin-powers');
    }
    if (forceTraceActive) {
      keys.add('force-traces');
    }
    return keys;
  }, [adminPowersChecked, forceTraceActive]);

  if (viewer.admin !== true) {
    return null;
  }

  const handleSelectionChange = (keys: Selection) => {
    if (keys === 'all') {
      return;
    }
    const adminPowersSelected = keys.has('admin-powers');
    if (adminPowersSelected !== adminPowersChecked) {
      setPendingAdminPowers(adminPowersSelected);
      setViewerUsingAdminPrivileges(!viewerUsingAdminPrivileges());
      window.setTimeout(() => window.location.reload(), 500);
      return;
    }
    const forceTracesSelected = keys.has('force-traces');
    if (forceTracesSelected !== forceTraceActive) {
      if (forceTraceActive) {
        disableForceTrace();
        setForceTraceExpiresAt(null);
        setForceTraceActive(isForceTraceActive(viewer.email));
      } else {
        enableForceTrace();
        const expiresAt = getForceTraceExpiresAt();
        setForceTraceExpiresAt(expiresAt);
        setForceTraceActive(isForceTraceActive(viewer.email));
      }
    }
  };

  const forceTraceText = forceTraceLabel(forceTraceExpiresAt, now);
  const onWandbPage =
    activeRouteClaim(routeClaims, pathname)?.remote.name === 'wandb';
  const wandbHomeHref = shellPathForRemotePath(routeClaims, 'wandb', '/home');
  const peerEnvironments = Object.entries(adminEnvironments()).filter(
    ([, origin]) => origin !== location.origin
  );

  return (
    <>
      <DropdownMenu.Separator />
      <DropdownMenu.Section>
        <DropdownMenu.Header>Forge admin tools</DropdownMenu.Header>
        {adminTools.shouldShowAdminTools && (
          <HeaderLink
            label="Admin pages"
            path="/admin/home"
            icon="admin-shield-safe"
            routeClaims={routeClaims}
            onAction={onClose}
          />
        )}
        {!onWandbPage && wandbHomeHref != null && (
          <DropdownMenu.Item href={wandbHomeHref} onAction={onClose}>
            <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'info'}} />
            <DropdownMenu.ItemLabel>
              More admin tools on W&B pages
            </DropdownMenu.ItemLabel>
            <DropdownMenu.ItemDescription>
              Request admin access, Use as..., and Simulate usage
            </DropdownMenu.ItemDescription>
          </DropdownMenu.Item>
        )}
        {adminTools.teleportAdminCheckIsEnabled &&
          actions.onRequestAdminAccessPress != null && (
            <DropdownMenu.Item
              onAction={() => {
                onClose();
                actions.onRequestAdminAccessPress?.();
              }}>
              <DropdownMenu.ItemDecorationLeft
                iconProps={{icon: 'key-admin'}}
              />
              <DropdownMenu.ItemLabel>
                Request admin access
              </DropdownMenu.ItemLabel>
            </DropdownMenu.Item>
          )}
        {adminTools.isAdminModeActive &&
          actions.onSimulateUsagePress != null && (
            <DropdownMenu.Item
              onAction={() => {
                onClose();
                actions.onSimulateUsagePress?.();
              }}>
              <DropdownMenu.ItemDecorationLeft
                iconProps={{icon: 'chart-line'}}
              />
              <DropdownMenu.ItemLabel>Simulate usage</DropdownMenu.ItemLabel>
            </DropdownMenu.Item>
          )}
        {adminTools.isAdminModeActive &&
          adminTools.shouldShowAdminTools &&
          actions.onImpersonatePress != null && (
            <DropdownMenu.Item
              onAction={() => {
                onClose();
                actions.onImpersonatePress?.();
              }}>
              <DropdownMenu.ItemDecorationLeft
                iconProps={{icon: 'impersonate-mask-alt'}}
              />
              <DropdownMenu.ItemLabel>Use as...</DropdownMenu.ItemLabel>
            </DropdownMenu.Item>
          )}
        {peerEnvironments.map(([name, origin]) => (
          <DropdownMenu.Item
            key={name}
            href={peerUrl(origin)}
            onAction={onClose}>
            <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'code-alt'}} />
            <DropdownMenu.ItemLabel>
              Open in {name} environment
            </DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Section>
      <DropdownMenu.Section
        selectionMode="multiple"
        selectedKeys={selectedKeys}
        onSelectionChange={handleSelectionChange}
        aria-label="Admin toggles">
        {adminTools.shouldShowAdminTools && (
          <DropdownMenu.Item id="admin-powers">
            <DropdownMenu.ItemDecorationLeft
              iconProps={{icon: 'magic-wand-stick'}}
            />
            <DropdownMenu.ItemLabel>Admin powers</DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        )}
        {adminTools.isInternalUser && (
          <DropdownMenu.Item id="force-traces">
            <DropdownMenu.ItemDecorationLeft iconProps={{icon: 'flash-bolt'}} />
            <DropdownMenu.ItemLabel>
              {forceTraceText
                ? `Force traces (${forceTraceText})`
                : 'Force traces'}
            </DropdownMenu.ItemLabel>
          </DropdownMenu.Item>
        )}
      </DropdownMenu.Section>
    </>
  );
}
